// ProjectBoard.js
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase.js";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  updateDoc,
  doc,
  getDocs,
  arrayUnion,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import { Paper, Card, CardContent, Typography, Box } from "@mui/material";

export default function ProjectBoard() {
  const { projectId, projectName } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]); // List of project members for assignment

  // Filter state
  const [selectedUser, setSelectedUser] = useState("All Users");
  const [visibleStatuses, setVisibleStatuses] = useState(
    new Set(["Pending", "In Progress", "On Hold", "Completed"])
  );

  // Check if mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to format timestamp
  const formatTs = (ts) => {
    if (!ts) return "—";
    let date;
    if (ts.seconds) {
      date = new Date(ts.seconds * 1000);
    } else {
      date = new Date(ts);
    }
    if (isNaN(date)) return "—";
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${day} ${month}, ${time}`;
  };

  // Helper function to calculate task age in days
  const calculateTaskAge = (createdAt) => {
    if (!createdAt) return null;

    let createdDate;

    if (createdAt.seconds) {
      createdDate = new Date(createdAt.seconds * 1000);
    } else {
      createdDate = new Date(createdAt);
    }

    if (isNaN(createdDate)) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    createdDate.setHours(0, 0, 0, 0);

    const diffTime = today - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day";
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} year${years !== 1 ? 's' : ''}`;
  };

  // Helper function to get current user's UID
  const getCurrentUserUID = () => {
    const user = auth.currentUser;
    return user?.uid || null;
  };

  // Fetch project members for assignment
  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!projectId) return;

      try {
        const projectDoc = await getDoc(doc(db, "projects", projectId));

        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const userIds = projectData.users || [];

          if (userIds.length > 0) {
            const usersRef = collection(db, "users");
            const usersSnapshot = await getDocs(usersRef);

            const members = [];
            usersSnapshot.forEach((userDoc) => {
              if (userIds.includes(userDoc.id)) {
                const userData = userDoc.data();
                members.push({
                  uid: userDoc.id,
                  name: userData.username || userData.email || "Unknown User",
                  email: userData.email || ""
                });
              }
            });

            setProjectMembers(members);
          }
        }
      } catch (error) {
        console.error("Error fetching project members:", error);
      }
    };

    fetchProjectMembers();
  }, [projectId]);

  // Update task assignment
  const updateTaskAssignment = async (taskId, assignedToUID) => {
    if (!taskId) return;

    try {
      const taskRef = doc(db, "projects", projectId, "tasks", taskId);
      const updateData = {
        updatedAt: serverTimestamp(),
      };

      if (assignedToUID) {
        updateData.assignedTo = assignedToUID;
        const assignedMember = projectMembers.find(m => m.uid === assignedToUID);
        if (assignedMember) {
          updateData.assignedToName = assignedMember.name;
          updateData.assignedToEmail = assignedMember.email;
        }
      } else {
        updateData.assignedTo = null;
        updateData.assignedToName = null;
        updateData.assignedToEmail = null;
      }

      await updateDoc(taskRef, updateData);
    } catch (error) {
      console.error("Error updating task assignment:", error);
      alert(`Failed to update assignment: ${error.message || "Unknown error"}`);
    }
  };

  // markSeen() removed - now called only when Chat button is clicked in Dashboard

  // Real-time listener for tasks
  useEffect(() => {
    if (!projectId) return;

    const tasksColPath = collection(db, "projects", projectId, "tasks");
    const q = query(
      tasksColPath,
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTasks(arr);
      },
      (err) => {
        console.error("Tasks snapshot error:", err);
      }
    );

    return () => unsub();
  }, [projectId]);

  // Compute unique users from tasks
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    tasks.forEach(task => {
      if (task.createdByName) users.add(task.createdByName);
      if (task.updatedBy) users.add(task.updatedBy);
    });
    return ["All Users", ...Array.from(users).sort()];
  }, [tasks]);

  // Filter tasks by selected user
  const filteredTasks = useMemo(() => {
    if (selectedUser === "All Users") {
      return tasks;
    }
    return tasks.filter(t =>
      t.createdByName === selectedUser ||
      t.updatedBy === selectedUser
    );
  }, [tasks, selectedUser]);

  // Toggle status visibility
  const toggleStatus = (status) => {
    setVisibleStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  // Filter tasks by status (case-insensitive matching) - now using filteredTasks
  const pendingTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "pending"
  );
  const inProgressTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "in progress" || t.status?.toLowerCase() === "progress"
  );
  const onHoldTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "on hold" || t.status?.toLowerCase() === "onhold"
  );
  const completedTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "completed"
  );

  // Render task card
  const renderTaskCard = (task) => (
    <TimelineItem key={task.id}>
      <TimelineSeparator>
        <TimelineDot color="success" />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent>
        <Paper
          elevation={0}
          style={{
            padding: "14px",
            borderRadius: "6px",
            marginBottom: "10px",
            maxWidth: "100%",
            border: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: "8px" }}>
            {task.text || "No description"}
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "4px" }}>
            Created By: <strong>{task.createdByName || "Unknown"}</strong>
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "4px" }}>
            Updated By: <strong>{task.updatedBy || "Unknown"}</strong>
          </Typography>

          {/* Show Assigned To - Only for Pending tasks */}
          {task.status === "Pending" && (
            <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "4px" }}>
              <span style={{ marginRight: "8px" }}>Assign to:</span>
              <select
                value={task.assignedTo || ""}
                onChange={(e) => updateTaskAssignment(task.id, e.target.value || null)}
                style={{
                  fontSize: "0.75rem",
                  padding: "2px 6px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  backgroundColor: task.assignedTo ? "#e8f5e9" : "white",
                  minWidth: "120px",
                }}
              >
                <option value="">Unassigned</option>
                <option value={getCurrentUserUID()}>Myself ({auth.currentUser?.displayName || auth.currentUser?.email || "Me"})</option>
                {projectMembers
                  .filter(m => m.uid !== getCurrentUserUID())
                  .map((member) => (
                    <option key={member.uid} value={member.uid}>
                      {member.name}
                    </option>
                  ))}
              </select>
            </Typography>
          )}

          {/* Show Assigned To (Read-only) for non-Pending tasks */}
          {task.status !== "Pending" && task.assignedTo && (
            <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "4px" }}>
              Assigned to: <strong>{task.assignedToName || "Unknown"}</strong>
            </Typography>
          )}

          {/* Show Task Age - Skip for Completed tasks */}
          {task.status !== "Completed" && task.createdAt && calculateTaskAge(task.createdAt) && (
            <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "4px", fontStyle: "italic" }}>
              Age: <strong style={{ color: "#6c757d" }}>{calculateTaskAge(task.createdAt)}</strong>
            </Typography>
          )}

          {/* Status Change History Log */}
          {task.statusHistory && Array.isArray(task.statusHistory) && task.statusHistory.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e5e7eb", marginBottom: "4px" }}>
              <div style={{ fontWeight: 500, marginBottom: "4px", color: "#374151", fontSize: "0.75rem" }}>Status History:</div>
              <div style={{ maxHeight: "80px", overflowY: "auto", fontSize: "0.7rem", lineHeight: "1.4" }}>
                {task.statusHistory
                  .slice()
                  .reverse()
                  .map((entry, idx) => (
                    <div key={idx} style={{ marginBottom: "3px" }}>
                      <span style={{ color: "#6b7280" }}>
                        {formatTs(entry.updatedAt)} - Changed to <strong style={{ color: "#374151" }}>{entry.status}</strong> by <strong style={{ color: "#374151" }}>{entry.updatedBy || "Unknown"}</strong>
                        {entry.previousStatus && entry.previousStatus !== entry.status && (
                          <span style={{ color: "#9ca3af" }}> (from {entry.previousStatus})</span>
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" display="block">
            {formatTs(task.updatedAt)}
          </Typography>
        </Paper>
      </TimelineContent>
    </TimelineItem>
  );

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f7fa", padding: "20px" }}>
      {/* Header */}
      <Box
        style={{
          background: "#ffffff",
          color: "#111827",
          padding: "20px 24px",
          marginBottom: "24px",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
        }}
      >
        <div>
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: "6px", fontSize: "1.75rem", color: "#111827" }}>
            Board View
          </Typography>
          <Typography variant="body2" style={{ color: "#6b7280", fontSize: "0.95rem" }}>
            {projectName || "Project"}
          </Typography>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: "#f3f4f6",
            color: "#374151",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            fontSize: "20px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            transition: "all 0.2s ease",
            fontWeight: "500",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#e5e7eb";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#f3f4f6";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          ←
        </button>
      </Box>

      {/* Filter Bar */}
      <Box
        style={{
          backgroundColor: "#ffffff",
          padding: "16px 20px",
          marginBottom: "20px",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "center",
          border: "1px solid #e5e7eb",
        }}
      >
        {/* User Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: isMobile ? "1 1 100%" : "0 1 auto" }}>
          <Typography variant="body2" style={{ fontWeight: 500, color: "#374151", whiteSpace: "nowrap", fontSize: "0.875rem" }}>
            User:
          </Typography>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: "2px solid #e5e7eb",
              backgroundColor: "white",
              fontSize: "14px",
              cursor: "pointer",
              outline: "none",
              minWidth: "160px",
              flex: isMobile ? 1 : "initial",
              transition: "all 0.15s ease",
              fontWeight: "500",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#2a8c7b";
              e.target.style.boxShadow = "0 0 0 3px rgba(42, 140, 123, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e5e7eb";
              e.target.style.boxShadow = "none";
            }}
          >
            {uniqueUsers.map(user => (
              <option key={user} value={user}>{user}</option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 auto", flexWrap: "wrap" }}>
          <Typography variant="body2" style={{ fontWeight: 500, color: "#374151", whiteSpace: "nowrap", fontSize: "0.875rem" }}>
            Show:
          </Typography>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {[
              { name: "Pending", color: "#fffbeb", textColor: "#92400e", borderColor: "#fbbf24" },
              { name: "In Progress", color: "#eff6ff", textColor: "#1e40af", borderColor: "#3b82f6" },
              { name: "On Hold", color: "#fef2f2", textColor: "#991b1b", borderColor: "#ef4444" },
              { name: "Completed", color: "#ecfdf5", textColor: "#065f46", borderColor: "#10b981" }
            ].map(status => (
              <button
                key={status.name}
                onClick={() => toggleStatus(status.name)}
                style={{
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: visibleStatuses.has(status.name) ? "1px solid " + status.borderColor : "1px solid #e5e7eb",
                  backgroundColor: visibleStatuses.has(status.name) ? status.color : "#ffffff",
                  color: visibleStatuses.has(status.name) ? status.textColor : "#6b7280",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                  boxShadow: visibleStatuses.has(status.name) ? "0 1px 2px rgba(0, 0, 0, 0.05)" : "none",
                }}
                onMouseEnter={(e) => {
                  if (!visibleStatuses.has(status.name)) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!visibleStatuses.has(status.name)) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                  }
                }}
              >
                {visibleStatuses.has(status.name) ? "✓ " : ""}{status.name}
              </button>
            ))}
          </div>
        </div>
      </Box>

      {/* Board Columns */}
      <div
        className="board-columns-container"
        style={{
          minHeight: "calc(100vh - 200px)",
        }}
      >
        {/* Pending Column */}
        {visibleStatuses.has("Pending") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 200px)",
              overflow: "hidden",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "default",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#eecf6aff",
                padding: "16px",
                borderBottom: "2px solid #d4a853",
              }}
            >
              <Typography variant="h6" style={{ fontWeight: 600, color: "#856404" }}>
                Pending
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pendingTasks.length} task{pendingTasks.length !== 1 ? "s" : ""}
              </Typography>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
              }}
            >
              {pendingTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "20px" }}>
                  No pending tasks
                </Typography>
              ) : (
                <Timeline position="right">
                  {pendingTasks.map((task) => renderTaskCard(task))}
                </Timeline>
              )}
            </CardContent>
          </Card>
        )}

        {/* In Progress Column */}
        {visibleStatuses.has("In Progress") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 200px)",
              overflow: "hidden",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "default",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#eff6ff",
                padding: "14px 16px",
                borderBottom: "1px solid #bfdbfe",
              }}
            >
              <Typography variant="h6" style={{ fontWeight: 600, color: "#1e40af", fontSize: "0.9375rem" }}>
                In Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {inProgressTasks.length} task{inProgressTasks.length !== 1 ? "s" : ""}
              </Typography>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
              }}
            >
              {inProgressTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "20px" }}>
                  No in-progress tasks
                </Typography>
              ) : (
                <Timeline position="right">
                  {inProgressTasks.map((task) => renderTaskCard(task))}
                </Timeline>
              )}
            </CardContent>
          </Card>
        )}

        {/* On Hold Column */}
        {visibleStatuses.has("On Hold") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 200px)",
              overflow: "hidden",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "default",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#fef2f2",
                padding: "14px 16px",
                borderBottom: "1px solid #fecaca",
              }}
            >
              <Typography variant="h6" style={{ fontWeight: 600, color: "#991b1b", fontSize: "0.9375rem" }}>
                On Hold
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {onHoldTasks.length} task{onHoldTasks.length !== 1 ? "s" : ""}
              </Typography>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
              }}
            >
              {onHoldTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "20px" }}>
                  No on-hold tasks
                </Typography>
              ) : (
                <Timeline position="right">
                  {onHoldTasks.map((task) => renderTaskCard(task))}
                </Timeline>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed Column */}
        {visibleStatuses.has("Completed") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 200px)",
              overflow: "hidden",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "default",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#ecfdf5",
                padding: "14px 16px",
                borderBottom: "1px solid #a7f3d0",
              }}
            >
              <Typography variant="h6" style={{ fontWeight: 600, color: "#065f46", fontSize: "0.9375rem" }}>
                Completed
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {completedTasks.length} task{completedTasks.length !== 1 ? "s" : ""}
              </Typography>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
              }}
            >
              {completedTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "20px" }}>
                  No completed tasks
                </Typography>
              ) : (
                <Timeline position="right">
                  {completedTasks.map((task) => renderTaskCard(task))}
                </Timeline>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

